import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { X, Send, FileText } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

/** Create/Edit dialog for a single Progress Feedback entry. */
export default function FeedbackFormDialog({
  open, onClose, onSaved,
  branchId,
  student,        // {id, full_name | name, last_name}
  existing,       // when editing, the full feedback doc
}) {
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [title, setTitle] = useState("");
  const [subTitle, setSubTitle] = useState("");
  const [feedback, setFeedback] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (existing) {
      setDate(existing.date || new Date().toISOString().slice(0, 10));
      setTitle(existing.title || "");
      setSubTitle(existing.sub_title || "");
      setFeedback(existing.feedback || "");
    } else {
      setDate(new Date().toISOString().slice(0, 10));
      setTitle(""); setSubTitle(""); setFeedback("");
    }
  }, [open, existing]);

  if (!open) return null;

  const studentName = (existing?.student_name)
    || student?.full_name
    || `${student?.name || ""} ${student?.last_name || ""}`.trim();

  const handleSave = async (submit) => {
    if (!date) { toast.error("Date is required"); return; }
    if (!title.trim()) { toast.error("Title is required"); return; }
    if (!feedback.trim()) { toast.error("Feedback is required"); return; }
    setSaving(true);
    try {
      if (existing) {
        await api.patch(`/progress-feedback/${existing.id}`, {
          date, title, sub_title: subTitle, feedback, submit,
        });
      } else {
        await api.post("/progress-feedback", {
          branch_id: branchId, student_id: student.id,
          date, title, sub_title: subTitle, feedback, submit,
        });
      }
      toast.success(submit ? "Submitted for approval" : "Saved as draft");
      onSaved?.();
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="pf-form-dialog">
      <div className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-brand-purple/10 flex items-start justify-between">
          <div>
            <h2 className="font-heading text-2xl font-bold text-foreground">{existing ? "Edit" : "Create"} Progress Feedback</h2>
            <p className="text-sm text-brand-inkMuted mt-0.5">For: <span className="font-heading font-semibold text-foreground uppercase">{studentName}</span></p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} data-testid="pf-form-close"><X className="w-5 h-5" /></Button>
        </div>

        <div className="p-5 space-y-4">
          <Field label="Date *">
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="rounded-xl h-11" data-testid="pf-date" />
          </Field>
          <Field label="Title *">
            <Input value={title} onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Improvement in Class Participation" className="rounded-xl h-11" data-testid="pf-title" />
          </Field>
          <Field label="Sub-title">
            <Input value={subTitle} onChange={(e) => setSubTitle(e.target.value)}
              placeholder="Optional" className="rounded-xl h-11" data-testid="pf-subtitle" />
          </Field>
          <Field label="Feedback *">
            <textarea value={feedback} onChange={(e) => setFeedback(e.target.value)}
              placeholder="Write parent-friendly feedback..." rows={7}
              className="w-full rounded-xl border border-brand-purple/20 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-purple/40"
              data-testid="pf-feedback" />
          </Field>
        </div>

        <div className="p-5 border-t border-brand-purple/10 flex flex-col sm:flex-row justify-end gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-2xl h-11" data-testid="pf-cancel">Cancel</Button>
          <Button variant="outline" disabled={saving} onClick={() => handleSave(false)} className="rounded-2xl h-11" data-testid="pf-save-draft">
            <FileText className="w-4 h-4 mr-1.5" /> Save as Draft
          </Button>
          <Button disabled={saving} onClick={() => handleSave(true)}
            className="rounded-2xl h-11 bg-brand-purple hover:bg-brand-purpleHover text-white" data-testid="pf-submit">
            <Send className="w-4 h-4 mr-1.5" /> Submit for Approval
          </Button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <p className="font-heading text-sm font-semibold text-foreground mb-1">{label}</p>
      {children}
    </div>
  );
}
